import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getVisibleCharacters, autoSetupCharacter, deleteCharacter, claimCharacterOwnership, generateAvatar, getVoices, getMinimaxVoices, bindVoice, getRoleCardSummaries, canManageCharacter, type DbCharacter, type DbVoice, type DbMinimaxVoice, type RoleCardSummary } from '@/api/astraea';
import { groupVoicesByGender } from '@/lib/voiceGroups';
import { isAdmin, getUserId } from '@/api/auth';

let previewAudio: HTMLAudioElement | null = null;
function playPreview(url: string) {
  try {
    if (previewAudio) previewAudio.pause();
    previewAudio = new Audio(url);
    previewAudio.play();
  } catch {
    /* ignore */
  }
}

async function downloadFromUrl(filename: string, url: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(u);
  } catch {
    window.open(url, '_blank');
  }
}

const PLATFORM_OPTIONS = [
  { value: 'xiaohongshu', label: '小红书' },
  { value: 'douyin', label: '抖音' },
  { value: 'kuaishou', label: '快手' },
  { value: 'bilibili', label: 'B站' },
  { value: 'wechat_channels', label: '微信视频号' },
  { value: 'zhihu', label: '知乎' },
  { value: 'wb58', label: '58同城' },
];

const platformLabelIdx: Record<string, string> = { xiaohongshu: '小红书', douyin: '抖音', kuaishou: '快手', bilibili: 'B站', wechat_channels: '视频号', zhihu: '知乎', wb58: '58同城' };

export default function BoardListPage() {
  const nav = useNavigate();
  const [characters, setCharacters] = useState<DbCharacter[]>([]);
  const [summaries, setSummaries] = useState<Record<number, RoleCardSummary>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [avatarBusyId, setAvatarBusyId] = useState<number | null>(null);
  const [admin, setAdmin] = useState(false);
  const [uid, setUid] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DbCharacter | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [voices, setVoices] = useState<DbVoice[]>([]);
  const [mmVoices, setMmVoices] = useState<DbMinimaxVoice[]>([]);
  const [voicePickerId, setVoicePickerId] = useState<number | null>(null);
  const [bindingVoiceId, setBindingVoiceId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [businessGoal, setBusinessGoal] = useState('');
  const [manualGender, setManualGender] = useState<'female' | 'male'>('female');
  const [manualSpecial, setManualSpecial] = useState('');
  const [platforms, setPlatforms] = useState<string[]>(['xiaohongshu']);

  const load = useCallback(() => {
    setLoading(true);
    getVisibleCharacters()
      .then((cs) => {
        setCharacters(cs);
        if (cs.length) getRoleCardSummaries(cs.map((c) => c.id)).then(setSummaries).catch(() => {});
      })
      .catch((e) => setError(e instanceof Error ? e.message : '读取失败'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    getVoices().then(setVoices).catch(() => {});
    getMinimaxVoices().then(setMmVoices).catch(() => {});
    isAdmin().then(setAdmin).catch(() => setAdmin(false));
    getUserId().then(setUid).catch(() => setUid(null));
  }, [load]);

  const canManage = (c: DbCharacter) => canManageCharacter(c, uid, admin);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteCharacter(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const handleBindVoice = async (characterId: number, voiceId: string) => {
    setBindingVoiceId(characterId);
    setError(null);
    try {
      await bindVoice(characterId, voiceId);
      setVoicePickerId(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '声音绑定失败');
    } finally {
      setBindingVoiceId(null);
    }
  };

  const togglePlatform = (v: string) => {
    setPlatforms((prev) => (prev.includes(v) ? prev.filter((p) => p !== v) : [...prev, v]));
  };

  const handleGenerateAvatar = async (id: number) => {
    setAvatarBusyId(id);
    setError(null);
    try {
      const r = await generateAvatar(id);
      if (r && r.ok === false) setError(r.message || '形象生成尚未完成，请再试一次');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '形象生成失败');
    } finally {
      setAvatarBusyId(null);
    }
  };

  const handleCreate = useCallback(async () => {
    if (!name.trim()) {
      setError('请填写角色名称');
      return;
    }
    if (platforms.length === 0) { setError('请至少选一个平台'); return; }
    setCreating(true);
    setError(null);
    try {
      // 手动建立也走 AI 补完人设:把用户填的描述/商业目标/特殊需求一起交给 AI 包装补完
      const seed = [description.trim(), businessGoal.trim() ? ('商业目标:' + businessGoal.trim()) : '', manualSpecial.trim()].filter(Boolean).join('\n');
      const r = await autoSetupCharacter(name.trim(), platforms, manualGender, seed);
      if (!r.character_id) throw new Error('建立失败,请再试一次');
      try { await claimCharacterOwnership(r.character_id); } catch { /* 认领失败不阻断 */ }
      setName(''); setDescription(''); setBusinessGoal(''); setManualSpecial(''); setManualGender('female');
      setPlatforms(['xiaohongshu']); setShowForm(false);
      // 进到该角色任务板:可先看「角色人设与背景」确认,再点生成策略
      nav(`/board/${r.character_id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : '建立失败');
    } finally {
      setCreating(false);
    }
  }, [name, description, businessGoal, manualGender, manualSpecial, platforms, nav]);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-[11px] font-medium mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
            资料即时同步
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">我的角色</h1>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
            每个角色就是一个正在跑的营销单位:这周要帮你吸引谁、各平台负责什么、私域怎么接、主动开发怎么做、做到哪了。点「查看本周策略」进角色作战台。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/reply"
            className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full text-sm font-medium whitespace-nowrap border border-secondary-200 text-foreground-600 hover:border-secondary-300 transition-colors cursor-pointer"
          >
            <i className="ri-chat-smile-2-line"></i>
            客户回复优化
          </Link>
          <Link
            to="/platforms"
            className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full text-sm font-medium whitespace-nowrap border border-secondary-200 text-foreground-600 hover:border-secondary-300 transition-colors cursor-pointer"
          >
            <i className="ri-book-2-line"></i>
            平台字典
          </Link>
          <Link
            to="/create"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 active:scale-95 transition-all cursor-pointer"
          >
            <i className="ri-magic-line"></i>
            一键新建角色
          </Link>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-full text-sm font-medium whitespace-nowrap border border-secondary-200 text-foreground-600 hover:border-secondary-300 transition-colors cursor-pointer"
          >
            <i className={showForm ? 'ri-close-line' : 'ri-edit-line'}></i>
            {showForm ? '取消' : '手动建立'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1.5"></i>
          {error}
        </div>
      )}

      {showForm && (
        <div className="mb-8 bg-background-100 rounded-xl border border-secondary-200 p-6 space-y-4">
          <h2 className="text-base font-semibold text-foreground-950">建立新角色</h2>
          <div>
            <label className="block text-xs font-medium text-secondary-500 mb-1.5">角色名称 *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如:小柔"
              className="w-full text-sm text-foreground-900 bg-background-50 border border-secondary-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-500 mb-1.5">性别</label>
            <div className="flex gap-2">
              {([['female', '女性'], ['male', '男性']] as const).map(([g, lb]) => (
                <button key={g} type="button" onClick={() => setManualGender(g)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium cursor-pointer border ${manualGender === g ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-secondary-200 text-secondary-500 hover:border-secondary-300'}`}>
                  <i className={`${g === 'female' ? 'ri-women-line' : 'ri-men-line'} mr-1`}></i>{lb}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-500 mb-1.5">角色描述(选填)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="例如:职场疗愈系陪伴博主"
              className="w-full text-sm text-foreground-900 bg-background-50 border border-secondary-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-500 mb-1.5">商业目标(选填)</label>
            <input
              type="text"
              value={businessGoal}
              onChange={(e) => setBusinessGoal(e.target.value)}
              placeholder="例如:信任养成"
              className="w-full text-sm text-foreground-900 bg-background-50 border border-secondary-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-500 mb-1.5">特殊需求(选填)</label>
            <p className="text-[11px] text-secondary-400 mb-1">想要的设定、背景、卖点、语气、禁忌…直接写(简短或口语也行),AI 会帮你包装、补完成完整人设。</p>
            <textarea value={manualSpecial} onChange={(e) => setManualSpecial(e.target.value)} rows={3} maxLength={1200}
              placeholder="例如:她是离职后做自由接案的设计师,想分享省钱和接案心得,语气轻松但专业,不要太鸡汤"
              className="w-full text-sm text-foreground-900 bg-background-50 border border-secondary-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-400 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-500 mb-2">目标平台</label>
            <div className="flex items-center gap-2">
              {PLATFORM_OPTIONS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => togglePlatform(p.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer border ${
                    platforms.includes(p.value)
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'border-secondary-200 text-secondary-500 hover:border-secondary-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="pt-2">
            <button
              onClick={handleCreate}
              disabled={creating}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                creating ? 'bg-primary-400 text-background-50 cursor-wait' : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>
                  AI 建立人设中…(约 1 分钟)
                </>
              ) : (
                <>
                  <i className="ri-magic-line"></i>
                  建立角色(AI 补完人设)
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-sm text-secondary-400">
          <div className="inline-block w-6 h-6 border-2 border-secondary-200 border-t-primary-500 rounded-full animate-spin mb-3"></div>
          <div>读取中…</div>
        </div>
      ) : characters.length === 0 ? (
        <div className="py-16 text-center text-sm text-foreground-600">
          你还没有自己的角色,点右上角「一键新建角色」建立一个吧。
        </div>
      ) : (
        <div className="space-y-4">
          {characters.map((char) => (
            <div
              key={char.id}
              className="bg-background-100 rounded-xl border border-secondary-200 p-5 md:p-6"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-primary-100 flex items-center justify-center flex-shrink-0">
                  {char.avatar_url ? (
                    <img src={char.avatar_url} alt={char.name} className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-user-smile-line text-primary-600 text-xl"></i>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-base font-semibold text-foreground-950">{char.name}</div>
                  <div className="text-xs text-foreground-600 mt-0.5 truncate">{char.description}</div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {(char.target_platforms ?? []).map((p) => (
                      <span key={p} className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-600 text-[10px] font-medium">
                        {PLATFORM_OPTIONS.find((o) => o.value === p)?.label ?? p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
                <span className="text-xs text-foreground-600 whitespace-nowrap">{char.business_goal || char.status}</span>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                {!char.avatar_url && (
                  <button
                    onClick={() => handleGenerateAvatar(char.id)}
                    disabled={avatarBusyId === char.id}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer border ${
                      avatarBusyId === char.id ? 'border-secondary-200 text-secondary-400 cursor-wait' : 'border-primary-300 text-primary-700 hover:bg-primary-50'
                    }`}
                  >
                    {avatarBusyId === char.id ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
                        生成形象中…
                      </>
                    ) : (
                      <>
                        <i className="ri-camera-line"></i>
                        生成真人形象
                      </>
                    )}
                  </button>
                )}
                <Link
                  to={`/board/${char.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-flag-2-line"></i>
                  {summaries[char.id]?.has_strategy ? '查看本周策略' : '生成本周策略'}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              </div>

              {(() => {
                const s = summaries[char.id];
                if (!s) return null;
                if (!s.has_strategy) {
                  return (
                    <div className="mt-4 pt-4 border-t border-secondary-100 rounded-b-xl">
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        <i className="ri-lightbulb-flash-line mr-1"></i>这个角色还没有本周策略。点「生成本周策略」,系统会建立平台分工、受众地图、私域承接与任务板。
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="mt-4 pt-4 border-t border-secondary-100 space-y-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-50 text-accent-700 text-[10px] font-semibold"><i className="ri-flag-2-line"></i>第 {s.phase} 阶段</span>
                      <span className="text-[11px] text-secondary-400">本周进度 内容 {s.content_done}/{s.content_total}</span>
                      <div className="flex-1 min-w-[80px] h-1.5 rounded-full bg-secondary-100 overflow-hidden max-w-[160px]">
                        <div className="h-full bg-primary-500" style={{ width: `${s.content_total ? Math.round((s.content_done / s.content_total) * 100) : 0}%` }}></div>
                      </div>
                    </div>
                    {s.weekly_goal && (
                      <div className="text-xs text-foreground-700 leading-relaxed"><span className="text-secondary-400">本周目标:</span> {s.weekly_goal}</div>
                    )}
                    {s.audiences.length > 0 && (
                      <div className="text-[11px] text-foreground-600"><i className="ri-group-line text-secondary-400 mr-1"></i>受众:{s.audiences.join(' / ')}</div>
                    )}
                    {s.platform_roles.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {s.platform_roles.map((p, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-background-50 border border-secondary-200 text-[10px] text-foreground-600" title={p.task}>
                            <span className="font-medium text-foreground-800">{platformLabelIdx[p.platform] || p.platform}</span>
                            <span className="text-secondary-400 truncate max-w-[140px]">{p.task}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {s.private_domain_path && (
                      <div className="text-[11px] text-purple-700"><i className="ri-route-line mr-1"></i>私域:{s.private_domain_path}</div>
                    )}
                    {s.outreach && (
                      <div className="text-[11px] text-blue-700"><i className="ri-radar-line mr-1"></i>开发:{s.outreach}</div>
                    )}
                    {s.next_best_action && (
                      <div className="text-[11px] text-primary-700 bg-primary-50/60 rounded px-2 py-1"><i className="ri-arrow-right-circle-line mr-1"></i>下一步:{s.next_best_action}</div>
                    )}
                  </div>
                );
              })()}

              <div className="mt-4 pt-4 border-t border-secondary-100">
                <div className="flex items-center gap-2 flex-wrap">
                  <i className="ri-volume-up-line text-secondary-500"></i>
                  <span className="text-xs text-secondary-500">声音:</span>
                  <span className="text-xs text-foreground-700">
                    {(() => {
                      if (!char.voice_id) return '未绑定';
                      const mm = mmVoices.find((v) => v.voice_id === char.voice_id);
                      if (mm) return `${mm.name || char.voice_id}${mm.language ? `(${mm.language})` : ''}`;
                      const hg = voices.find((v) => v.voice_id === char.voice_id);
                      return hg?.name || '已绑定';
                    })()}
                  </span>
                  {char.voice_id && voices.find((v) => v.voice_id === char.voice_id)?.preview_url && (
                    <button
                      onClick={() => playPreview(voices.find((v) => v.voice_id === char.voice_id)!.preview_url as string)}
                      className="inline-flex items-center gap-1 text-[11px] text-primary-600 hover:text-primary-700 cursor-pointer"
                    >
                      <i className="ri-play-circle-line"></i>试听
                    </button>
                  )}
                  <div className="ml-auto flex items-center gap-1.5 flex-wrap">
                    <button
                      onClick={() => setVoicePickerId(voicePickerId === char.id ? null : char.id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-secondary-200 text-foreground-600 hover:border-secondary-300 cursor-pointer"
                    >
                      <i className="ri-mic-line"></i>
                      {char.voice_id ? '更换声音' : '绑定声音'}
                    </button>
                    {char.avatar_url && (
                      <button
                        onClick={() => downloadFromUrl(`${char.name || 'avatar'}.png`, char.avatar_url as string)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-secondary-200 text-foreground-600 hover:border-secondary-300 cursor-pointer"
                      >
                        <i className="ri-download-line"></i>下载形象
                      </button>
                    )}
                    {canManage(char) && (
                      <button
                        onClick={() => setDeleteTarget(char)}
                        title="删除角色"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-red-200 text-red-600 hover:bg-red-50 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>删除
                      </button>
                    )}
                  </div>
                </div>
                {voicePickerId === char.id && (() => {
                  const { female, male, other } = groupVoicesByGender(voices);
                  const VoiceCard = (v: DbVoice) => (
                    <div key={v.voice_id} className="flex items-center gap-2 p-2 rounded-lg border border-secondary-200 bg-background-50">
                      <button
                        onClick={() => v.preview_url && playPreview(v.preview_url)}
                        className="w-7 h-7 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-primary-100"
                        title="试听"
                      >
                        <i className="ri-play-fill"></i>
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-foreground-800 truncate">{v.name}</div>
                      </div>
                      <button
                        onClick={() => handleBindVoice(char.id, v.voice_id)}
                        disabled={bindingVoiceId === char.id || char.voice_id === v.voice_id}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap cursor-pointer ${
                          char.voice_id === v.voice_id ? 'bg-green-50 text-green-700' : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                        }`}
                      >
                        {char.voice_id === v.voice_id ? '使用中' : '选用'}
                      </button>
                    </div>
                  );
                  return (
                    <div className="mt-3 space-y-3">
                      {female.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold text-foreground-700 mb-1.5">女声 ({female.length})</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">{female.map(VoiceCard)}</div>
                        </div>
                      )}
                      {male.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold text-foreground-700 mb-1.5">男声 ({male.length})</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">{male.map(VoiceCard)}</div>
                        </div>
                      )}
                      {other.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold text-foreground-700 mb-1.5">其他 ({other.length})</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">{other.map(VoiceCard)}</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="w-full max-w-md bg-background-50 rounded-2xl shadow-xl border border-secondary-200 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-950">确定删除吗？</h3>
                <p className="text-sm text-foreground-600 mt-0.5">即将删除角色「{deleteTarget.name}」</p>
              </div>
            </div>
            <div className="rounded-lg bg-red-50 border border-red-100 p-3.5 text-[13px] text-red-700 leading-relaxed space-y-1.5">
              <div><i className="ri-close-circle-line mr-1"></i>这个动作<b>无法复原</b>。</div>
              <div><i className="ri-coins-line mr-1"></i>先前花在这个角色的<b>点数不会退回</b>。</div>
              <div><i className="ri-folder-warning-line mr-1"></i>角色的周策略、任务、文案、影片、配音、形象都会<b>一并删除</b>。</div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2.5">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-secondary-200 text-foreground-600 hover:border-secondary-300 cursor-pointer disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer ${
                  deleting ? 'bg-red-400 text-white cursor-wait' : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {deleting ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>删除中…</>
                ) : (
                  <><i className="ri-delete-bin-line"></i>确定删除</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
